import React from 'react';
import { ShieldCheck, Cpu, Lock, LifeBuoy, Copy } from 'lucide-react';
import { sessionUser } from '../mock';
import { toast } from 'sonner';

const Row = ({ label, value, mono }) => (
  <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-[#FAFAF5]/60 border border-[#E8E5DC]">
    <div className={`text-[13px] ${mono ? 'font-mono-tight' : ''} text-[#0B1F2D]`}>{label}</div>
    <div className={`text-[13px] ${mono ? 'font-mono-tight' : ''} text-[#0B1F2D]/65`}>{value}</div>
  </div>
);

export default function SettingsPage() {
  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success('Copied to clipboard');
  };

  const cards = [
    {
      Ico: ShieldCheck,
      key: 'session',
      label: 'Session',
      title: 'Current authenticated session',
      content: (
        <div className="space-y-2.5">
          <Row label="Status" value="Authenticated" />
          <Row label="Role" value={sessionUser.role} mono />
          <Row label="Email" value={sessionUser.email} />
        </div>
      ),
    },
    {
      Ico: Cpu,
      key: 'engine',
      label: 'Engine',
      title: 'ESG-as-Code® rules engine. All logic server-side.',
      content: (
        <div className="space-y-2.5">
          <Row label="Version" value="ESGine® v1.0" mono />
          <Row label="Environment" value="Production" />
          <Row label="Mode" value="Deterministic" />
        </div>
      ),
    },
    {
      Ico: Lock,
      key: 'security',
      label: 'Security',
      title: 'Session data is held in memory only and expires when you close the browser tab.',
      content: (
        <div className="space-y-2.5">
          <Row label="State" value="Secured" />
          <Row label="Expires" value="On tab close" />
          <Row label="Transport" value="TLS 1.3" mono />
        </div>
      ),
    },
    {
      Ico: LifeBuoy,
      key: 'support',
      label: 'Support',
      title: 'For access issues or technical support.',
      content: (
        <div className="space-y-2.5">
          <button
            onClick={() => copy('info@esgine.io')}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-[#FAFAF5]/60 border border-[#E8E5DC] hover:border-[#0B1F2D] transition-colors group"
          >
            <span className="font-mono-tight text-[13px] text-[#0B1F2D]">info@esgine.io</span>
            <Copy className="w-3.5 h-3.5 text-[#0B1F2D]/45 group-hover:text-[#0B1F2D]" />
          </button>
          <Row label="Response" value="Within 1 business day" />
          <Row label="Hours" value="Mon-Fri · 9-18 UTC" mono />
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-[1280px]">
      <div className="mb-8">
        <div className="text-[11.5px] tracking-[0.16em] uppercase text-[#1B6F5E] font-medium">
          Account
        </div>
        <h2 className="mt-2 font-display text-[36px] lg:text-[44px] leading-[1.05] text-[#0B1F2D]">
          Settings.
        </h2>
        <p className="mt-3 text-[14.5px] leading-[1.55] text-[#0B1F2D]/65 max-w-[640px]">
          Account and session information. Your data is held in memory only and expires on tab close.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {cards.map((c) => (
          <div key={c.key} className="card-lift rounded-2xl border border-[#E8E5DC] bg-white p-7">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#E8F3EE] text-[#1B6F5E] flex items-center justify-center">
                <c.Ico className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#0B1F2D]/55">{c.label}</div>
                <div className="font-display text-[20px] leading-[1.15] text-[#0B1F2D]">{c.title.split('.')[0]}</div>
              </div>
            </div>
            <p className="mt-4 text-[12.5px] leading-[1.55] text-[#0B1F2D]/60">{c.title}</p>
            <div className="mt-5">{c.content}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
